import * as React from "react"
import { useParams, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import { useAuth, type AdminPermissions, type UserRole } from "@/contexts/AuthContext"
import { AdminSidebar, type AdminTab } from "@/components/admin/AdminSidebar"
import { AdminOverview } from "@/components/admin/AdminOverview"
import { AdminDeals } from "@/components/admin/AdminDeals"
import { AdminAgents } from "@/components/admin/AdminAgents"
import { AdminAgencies } from "@/components/admin/AdminAgencies"
import { AdminChat } from "@/components/admin/AdminChat"
import { AdminEvents } from "@/components/admin/AdminEvents"
import { AdminEconomics } from "@/components/admin/AdminEconomics"
import { AdminSettings } from "@/components/admin/AdminSettings"
import { AdminStore } from "@/components/admin/AdminStore"
import { AdminSupport } from "@/components/admin/AdminSupport"
import { StreaksAdmin } from "@/components/admin/StreaksAdmin"
import { PokerRoomTab } from "@/components/admin/PokerRoomTab"
import { AdminAirdrop } from "@/components/admin/AdminAirdrop"
import { AdminRedemptions } from "@/components/admin/AdminRedemptions"

const validTabs: AdminTab[] = ["overview", "streaks", "deals", "agents", "agencies", "chat", "events", "economics", "poker", "airdrop", "redemptions", "store", "support", "settings"]

export function AdminPage() {
  const { user, getAdminSession } = useAuth()
  const { tab } = useParams<{ tab?: string }>()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [role, setRole] = React.useState<UserRole | null>(null)
  const [permissions, setPermissions] = React.useState<AdminPermissions | null>(null)

  const activeTab: AdminTab = validTabs.includes(tab as AdminTab) ? (tab as AdminTab) : "overview"

  const handleTabChange = React.useCallback(
    (newTab: AdminTab) => {
      if (newTab === "overview") {
        navigate("/admin", { replace: true })
      } else {
        navigate(`/admin/${newTab}`, { replace: true })
      }
    },
    [navigate]
  )

  React.useEffect(() => {
    const loadSession = async () => {
      setIsLoading(true)
      setError(null)

      const result = await getAdminSession()
      if (!result.success || !result.permissions || !result.role) {
        setError(result.message || "You do not have permission to access the admin area.")
        setIsLoading(false)
        return
      }

      setRole(result.role)
      setPermissions(result.permissions)
      setIsLoading(false)
    }

    loadSession()
  }, [getAdminSession])

  if (isLoading) {
    return (
      <div className="admin-theme min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/30 font-admin">Verifying admin session...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-theme min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <div className="bg-[#111117] border border-red-500/20 rounded-xl p-6 max-w-md text-center">
          <p className="text-lg font-semibold text-red-400 mb-2 font-admin">Access Denied</p>
          <p className="text-sm text-white/40 font-admin">{error}</p>
          <a
            href="/dashboard"
            className="inline-block mt-4 px-4 py-2 rounded-lg bg-white/[0.06] text-sm text-white/60 hover:text-white/80 transition-colors font-admin"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-theme h-dvh min-h-dvh overflow-hidden bg-[#09090B] flex">
      <AdminSidebar activeTab={activeTab} onTabChange={handleTabChange} role={role} />

      <main className="flex-1 min-w-0 min-h-0 h-full overflow-y-auto overflow-x-hidden pt-14 lg:pt-0">
        <div className="p-4 pb-8 md:p-6 md:pb-8 lg:p-8 max-w-[1400px] mx-auto">
          {/* Page Title */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-6"
          >
            <h2 className="text-xl font-semibold text-white font-admin capitalize">
              {activeTab === "overview"
                ? "Overview"
                : activeTab === "streaks"
                  ? "Streaks"
                  : activeTab === "deals"
                    ? "Deal Analytics"
                    : activeTab === "chat"
                      ? "Chat Activity"
                      : activeTab === "events"
                        ? "Events"
                        : activeTab === "poker"
                          ? "Poker Room"
                          : activeTab === "economics"
                            ? "Oval Club Economics"
                            : activeTab === "airdrop"
                              ? "Airdrop"
                              : activeTab === "redemptions"
                                ? "Redemptions"
                                : activeTab === "store"
                                  ? "Store"
                                  : activeTab === "support"
                                    ? "Support"
                                    : activeTab}
            </h2>
            <p className="text-sm text-white/25 mt-0.5 font-admin">
              {activeTab === "overview" && "Key metrics and deal volume trends"}
              {activeTab === "streaks" && "Create and manage streak campaigns with milestones and rewards"}
              {activeTab === "deals" && "Deal trends and auto-approved submissions"}
              {activeTab === "agents" && "Agent performance and rankings"}
              {activeTab === "agencies" && "Agency metrics and comparisons"}
              {activeTab === "chat" && "Message volume and system broadcasts"}
              {activeTab === "events" && "Create and schedule events for the dashboard Events experience"}
              {activeTab === "poker" && "Manage poker rooms, bots, game rules, and live monitoring"}
              {activeTab === "economics" && "Edit all Oval Club rules, payouts, games, throttles, and eligibility gates"}
              {activeTab === "airdrop" && "Send pending balance to all users or specific accounts"}
              {activeTab === "redemptions" && "Review and process user redemption requests"}
              {activeTab === "store" && "Manage physical store products, inventory, imagery, and merchandising visibility"}
              {activeTab === "support" && "Lookup customers and launch investigate mode to mirror their experience"}
              {activeTab === "settings" && "Role permissions and session details"}
            </p>
          </motion.div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {activeTab === "overview" && <AdminOverview />}
              {activeTab === "streaks" && <StreaksAdmin />}
              {activeTab === "deals" && <AdminDeals />}
              {activeTab === "agents" && <AdminAgents />}
              {activeTab === "agencies" && <AdminAgencies />}
              {activeTab === "chat" && <AdminChat />}
              {activeTab === "events" && <AdminEvents />}
              {activeTab === "poker" && <PokerRoomTab />}
              {activeTab === "economics" && <AdminEconomics permissions={permissions} />}
              {activeTab === "airdrop" && <AdminAirdrop />}
              {activeTab === "redemptions" && <AdminRedemptions />}
              {activeTab === "store" && <AdminStore />}
              {activeTab === "support" && <AdminSupport />}
              {activeTab === "settings" && (
                <AdminSettings
                  email={user?.email || ""}
                  role={role}
                  permissions={permissions}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  )
}
